import React, { Suspense, useState } from 'react';

import { useIntl } from 'react-intl';

import { FlagGroup } from '@atlaskit/flag';
import { getProductReleaseVersion } from '@atlassian/search-common/build';
import type { ExperimentConfig } from '@atlassian/search-experiment/types';

import { type ApplicationModes } from '../../constants/application-modes';
import { isHello } from '../../utils/is-hello';

import { BUILD_VERSION, ENTRYPOINT_ID, FEEDBACK_CONTEXT_CF } from './constants';
import { getFeedbackCollectorContext } from './utils';

export const AkFeedbackCollector: React.LazyExoticComponent<
	typeof import('@atlaskit/feedback-collector').default
> = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/feedback-collector" */ '@atlaskit/feedback-collector'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

const AkFeedbackFlag = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

export type FeedbackCollectorProps = {
	cloudId: string;
	experimentConfig: ExperimentConfig;
	isOpen: boolean;
	primaryProduct: string;
	product: string;
	setIsOpen: (isOpen: boolean) => void;
	source: string;
	userId: string;
	userName: string;
	mode: ApplicationModes;
	requestId: string;
	traceId: string;
};

const FeedbackCollector = ({
	cloudId,
	experimentConfig,
	isOpen,
	primaryProduct,
	product,
	setIsOpen,
	source,
	userId,
	userName,
	mode,
	requestId,
	traceId,
}: FeedbackCollectorProps): React.JSX.Element => {
	const { locale } = useIntl();

	const [displayFlag, setDisplayFlag] = useState(false);

	return (
		<>
			{isOpen && (
				<Suspense fallback={<></>}>
					<AkFeedbackCollector
						locale={locale}
						onClose={() => setIsOpen(false)}
						onSubmit={() => setDisplayFlag(true)}
						atlassianAccountId={userId}
						name={userName}
						entrypointId={ENTRYPOINT_ID}
						additionalFields={[
							{
								id: FEEDBACK_CONTEXT_CF,
								value: getFeedbackCollectorContext({
									experimentLayers: experimentConfig.backendExperiment?.experimentLayers,
									frontendExperiments: experimentConfig.frontendExperiments,
									version: BUILD_VERSION,
									release: getProductReleaseVersion(primaryProduct),
									source,
									product: product,
									primaryProduct,
									// JQL has an issue with "contains" search. Use this value to filter out JQL feedback.
									primaryProductFilterString: `primaryProduct${primaryProduct}`,
									cloudId,
									userId,
									isHello: isHello(cloudId),
									mode,
									requestId,
									traceId,
								}),
							},
						]}
					/>
				</Suspense>
			)}
			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<Suspense fallback={<></>}>
						<AkFeedbackFlag locale={locale} />
					</Suspense>
				</FlagGroup>
			)}
		</>
	);
};

export default FeedbackCollector;
